import { TableCell, Checkbox, Tooltip, IconButton } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import HistoryIcon from "@mui/icons-material/History";
import type {
  breakdownProcess,
  exchangeRatePart,
} from "@/api/breakdownExchangeRateService";
import { parseDecimal } from "@/utils/inputParse";

interface ViewRowProps {
  part: exchangeRatePart;
  selectedParts: exchangeRatePart[];
  handleSelect: (part: exchangeRatePart, checked: boolean) => void;
  handleFlagPart: (partInfo: exchangeRatePart) => () => void;
  handleOpenHistory: (partId: number, partNumber?: string) => () => void;
  canEdit: boolean;
  oemProcess: breakdownProcess[];
  spareProcess: breakdownProcess[];
  isOpenOEMOtherProcess: boolean;
  isOpenSpareOtherProcess: boolean;
}

const ViewRow = ({
  part,
  selectedParts,
  handleSelect,
  handleFlagPart,
  handleOpenHistory,
  canEdit,
  oemProcess,
  spareProcess,
  isOpenOEMOtherProcess,
  isOpenSpareOtherProcess,
}: ViewRowProps) => {
  return (
    <>
      {/* Checkbox */}
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 101,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          backgroundColor: "inherit",
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
        }}
      >
        <Checkbox
          onChange={(e) => handleSelect(part, e.target.checked)}
          checked={selectedParts.some((p) => p.part_id === part.part_id)}
        />
      </TableCell>
      {/* Action */}
      <TableCell
        sx={{
          position: "sticky",
          left: 70,
          zIndex: 101,
          width: 120,
          minWidth: 120,
          maxWidth: 120,
          textAlign: "center",
          backgroundColor: "inherit",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <Tooltip title="Flag" placement="top">
          <IconButton disabled={!canEdit} onClick={handleFlagPart(part)}>
            <FlagIcon
              sx={{
                color: part.is_flagged
                  ? part.is_admin_flagged
                    ? "success.light"
                    : "error.main"
                  : "action.disabled",
              }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="History" placement="top">
          <IconButton
            onClick={handleOpenHistory(part.part_id, part.part_number)}
          >
            <HistoryIcon color="primary" />
          </IconButton>
        </Tooltip>
      </TableCell>
      {/* Part No. */}
      <TableCell
        sx={{
          position: "sticky",
          left: 190,
          zIndex: 101,
          width: 200,
          minWidth: 200,
          maxWidth: 200,
          textAlign: "left",
          backgroundColor: "inherit",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {part.part_number}
      </TableCell>
      {/* Part Name */}
      <TableCell
        sx={{
          position: "sticky",
          left: 390,
          zIndex: 101,
          width: 200,
          minWidth: 200,
          maxWidth: 200,
          textAlign: "left",
          backgroundColor: "inherit",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {part.part_name}
      </TableCell>
      {/* AC */}
      <TableCell
        sx={{
          position: "sticky",
          left: 590,
          zIndex: 101,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          backgroundColor: "inherit",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {part.ac}
      </TableCell>
      {/* Routing */}
      <TableCell
        sx={{
          position: "sticky",
          left: 660,
          zIndex: 101,
          width: 90,
          minWidth: 90,
          maxWidth: 90,
          textAlign: "center",
          backgroundColor: "inherit",
          borderRight: "2px solid #e0e0e0",
        }}
      >
        {part.rt}
      </TableCell>
      {/* OEM Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "2px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.oem_price ? parseDecimal(part.oem_price, 2) : "0.00"}
      </TableCell>
      {/* Spare Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "2px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.spare_price ? parseDecimal(part.spare_price, 2) : "0.00"}
      </TableCell>{" "}
      {/* Currency */}
      <TableCell
        sx={{
          minWidth: 300,
          maxWidth: 300,
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {part.currency_code || "-"}
      </TableCell>{" "}
      {/* Exchange Rate */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.exchange_rate || "0.00"}
      </TableCell>
      {/* Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.price || "0.00"}
      </TableCell>
      {/* Part Price */}
      <TableCell
        sx={{
          minWidth: 150,
          maxWidth: 150,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.part_price ? parseDecimal(part.part_price, 2) : "0.00"}
      </TableCell>
      {/* OEM Other Process */}
      <TableCell
        sx={{
          minWidth: 200,
          maxWidth: 200,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.total_oem_other_process_cost
          ? parseDecimal(part.total_oem_other_process_cost, 2)
          : "0.00"}
      </TableCell>
      {isOpenOEMOtherProcess &&
        oemProcess?.map((process) => {
          const processCost = part.oem_other_process?.find(
            (p) => p.id === process.id,
          )?.cost;
          return (
            <TableCell
              key={process.id}
              sx={{
                minWidth: 150,
                maxWidth: 150,
                textAlign: "right",
                borderRight: "1px solid #e0e0e0",
                fontSize: "0.9rem",
              }}
            >
              {processCost || "0.00"}
            </TableCell>
          );
        })}
      {/* Spare Other Process */}
      <TableCell
        sx={{
          minWidth: 200,
          maxWidth: 200,
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
          fontSize: "0.9rem",
        }}
      >
        {part.total_spare_other_process_cost
          ? parseDecimal(part.total_spare_other_process_cost, 2)
          : "0.00"}
      </TableCell>
      {isOpenSpareOtherProcess &&
        spareProcess?.map((process) => {
          const processCost = part.spare_other_process?.find(
            (p) => p.id === process.id,
          )?.cost;
          return (
            <TableCell
              key={process.id}
              sx={{
                minWidth: 150,
                maxWidth: 150,
                textAlign: "right",
                borderRight: "1px solid #e0e0e0",
                fontSize: "0.9rem",
              }}
            >
              {processCost || "0.00"}
            </TableCell>
          );
        })}
    </>
  );
};

export default ViewRow;
